import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { QuarterSelectorType, QuarterSelectorValue } from '@cbre/components';
import { L10nTranslationService } from 'angular-l10n';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { auditTime, of, Subject, takeUntil } from 'rxjs';
import { DefaultSelectionValue, FilterInputType, FormGroupValue, InputProps } from './input-props';
import { SimpleFormGroupService } from './simple-form-group.service';
import { updateValues } from './filter-bar.helper';

@UntilDestroy()
@Component({
  selector: 'miq-filters-bar',
  templateUrl: './filters-bar.component.html',
  styleUrls: ['./filters-bar.component.scss'],
  standalone: false,
})
export class FiltersBarComponent<T = DefaultSelectionValue> implements OnChanges, OnDestroy, OnInit {
  @Input() addBackLink = false;
  @Input() isLandingPage = false;
  @Input() isReport = false;
  // TODO l10n: figure how to translate options (if needed) and labels
  @Input() filters: Array<InputProps<T>> = [];
  @Input() backLinkRoute: string[];
  @Input() isDownloadReportList = false;
  @Input() reportType?: string;
  @Input() disableAll = false;
  @Output() selection = new EventEmitter<FormGroupValue<T>>();
  #disconnectSelectionEvent = new Subject();
  // emit individual changes for cases when specific fields triggers custom events
  @Output() change = new EventEmitter<{ [key: string]: any }>();
  @Output() formReady = new EventEmitter<UntypedFormGroup>();
  @Output() clearFilters = new EventEmitter<any>();
  @Output() updateView = new EventEmitter<any>();

  form: UntypedFormGroup;
  filterInputTypeEnum = FilterInputType; // enum assignment for template usage
  reportId: number;
  currentQtdValue: number;
  marketQtdValue: number;
  periodRangeValue: any;
  periodType: string = QuarterSelectorType.Quarter;
  maxValue: QuarterSelectorValue | Date = new Date();

  constructor(
    private fb: UntypedFormBuilder,
    private simpleFormGroupService: SimpleFormGroupService,
    public translationService: L10nTranslationService,
    private route: ActivatedRoute
  ) {}

  ngOnChanges({ filters }: SimpleChanges) {
    if (!!filters) {
      this.initForm();
      this.filters = this.filters.map(filter => ({ ...filter, label: this.#getFilterLabel(filter) }));
    }
  }

  #getFilterLabel(filter: InputProps<T>): string {
    if (filter.type !== FilterInputType.Range) return filter.label;
    // if not range
    return filter.isLabelTranslated ? filter.label : this.translationService.translate(filter.label);
  }

  ngOnInit() {
    /* required by translation */
    this.reportId = this.route.snapshot.params.idReport;
    if (this.form.controls.markets) {
      this.marketQtdValue = Number(this.form.controls.markets.value);
      this.periodRangeValue = this.form.controls.periodRange?.value;
    }
  }

  ngOnDestroy() {
    /* REQUIRED by untilDestroyed and translation */
  }

  initForm() {
    this.#disconnectSelectionEvent.next(true);
    this.form = this.fb.group(this.simpleFormGroupService.makeGroup<T>(this.inputs));
    this.periodType = this.form.controls.periodType && this.form.controls.periodType.value;
    const today = new Date();
    this.maxValue = today;
    this.form.valueChanges
      .pipe(takeUntil(this.#disconnectSelectionEvent), untilDestroyed(this), auditTime(300))
      .subscribe((value: FormGroupValue<T>) => this.selection.emit(value));
    this.formReady.emit(this.form);
  }

  emitChangeAndPatchForm(change: { [key: string]: any }) {
    this.form.patchValue(change);
    const divisionCtrl = this.form.get('divisions');
    if (divisionCtrl && change?.divisions != null) {
      const divisionsFilter = this.filters.find(filter => filter.field === 'divisions');
      const market = this.filters.find(filter => filter.field === 'market');
      const marketCtrl = this.form.get('market');
      marketCtrl.setValue([]);
      market.value = null;
      if (divisionCtrl?.value?.length > 0) {
        const divisionKeys = change.divisions
          .map(division => divisionsFilter.options.find(option => division === option.value)?.key)
          .filter(Boolean);
        market.optionsEndpoint = this.simpleFormGroupService.getMarkets(divisionKeys);
        market.disabled = false;
      } else {
        market.disabled = true;
      }
    }
    if (this.form.controls.periodType && this.form.controls.periodType.value) {
      localStorage.setItem('userPreferredPeriodType', this.form.get('periodType').value);
    }

    if (change?.markets != null) {
      this.marketQtdValue = Number(change.markets);
      if (this.form.controls.subMarket) {
        this.form.controls.subMarket.setValue(null);
        this.resetSubmarketDistrictOptions(change);
      }
      if (this.form.controls.district) {
        this.form.controls.district.setValue(null);
      }
    }
    if (change && change.subMarket !== undefined) {
      if (this.form.controls.district) {
        this.resetSubmarketDistrictOptions(change);
        this.form.controls.district.setValue(null);
      }
    }
    if (change && change.periodType != undefined && this.form.controls.periodRange) {
      const today = new Date();
      this.maxValue = today;
      const maxYear = today.getFullYear();
      const val = this.form.controls.periodRange.value;
      const changedFromYear = this.periodType === QuarterSelectorType.Yearly;
      this.periodType = change.periodType.toString();

      updateValues(val?.eq, change.periodType, maxYear, today, changedFromYear);
      updateValues(val?.gte, change.periodType, maxYear, today, changedFromYear);
      updateValues(val?.lte, change.periodType, maxYear, today, changedFromYear);

      this.periodRangeValue = val;
      this.form.controls.periodRange.setValue(val);
    }
    this.change.emit(change);
  }

  resetSubmarketDistrictOptions(change) {
    const subMarket = this.filters.find(filter => filter.field === 'subMarket');
    const district = this.filters.find(filter => filter.field === 'district');
    if (change.markets && subMarket) {
      subMarket.value = null;
      subMarket.optionsEndpoint = this.simpleFormGroupService.getGeoMarkets('submarkets', change.markets);
      if (district) {
        district.value = null;
        district.optionsEndpoint = of(null);
      }
    }
    if (change.subMarket !== undefined && district) {
      district.value = null;
      district.optionsEndpoint = change.subMarket
        ? this.simpleFormGroupService.getGeoMarkets('districts', change.subMarket)
        : of(null);
    }
  }

  get inputs(): Array<InputProps<T>> {
    return this.filters.filter(({ type }) => type !== FilterInputType.Separator);
  }

  onSubmit() {
    this.updateView.emit(this.form);
  }
}
